import type { TestPlayer } from '../../lib/api/operatorConsole';
import { Badge } from './Badge';
import { canLiftMark } from './canLiftMark';
import { MatchBadge } from './MatchBadge';

interface Props {
  player: TestPlayer;
  canEdit: boolean;
  selected: boolean;
  onSelectChange: (player: TestPlayer, selected: boolean) => void;
  onDeactivate: (player: TestPlayer) => void;
}

// Who is behind the mark. `operator` is the partner reading this screen.
const ADDED_BY_LABEL: Record<string, string> = {
  operator: 'You',
  admin: 'Repackz',
  system: 'Repackz (automatic)',
};

// How it got in, which is a different question from who put it there.
const SOURCE_LABEL: Record<string, string> = {
  admin_ui: 'Manual entry',
  admin_csv: 'CSV import',
  operator_api: 'Manual entry',
  operator_csv: 'CSV import',
  system: 'Automatic',
};

const LOCKED_TITLE =
  'Repackz placed this mark, so only Repackz can lift it. Contact us if it should not be here.';

const WITHHELD_REASON_TITLE =
  'The note behind a Repackz mark is our internal audit trail and is not shared.';

const cell = 'px-4 py-3 align-middle text-[13px] text-[var(--co-text)]';
const mutedCell = 'px-4 py-3 align-middle text-[13px] text-[var(--co-muted)]';

// `reason` is absent on our rows and present (possibly null) on the operator's,
// so an empty cell must not read the same in both cases.
function ReasonCell({ player }: { player: TestPlayer }) {
  if (player.reason === undefined) {
    return (
      <td className={`${mutedCell} italic`} title={WITHHELD_REASON_TITLE}>
        Internal note, not shared
      </td>
    );
  }

  return <td className={mutedCell}>{player.reason || '—'}</td>;
}

export function TestPlayerRow({
  player,
  canEdit,
  selected,
  onSelectChange,
  onDeactivate,
}: Props) {
  const active = player.status === 'active';
  const liftable = canLiftMark(player);

  return (
    <tr
      className="border-b border-[var(--co-border)]"
      // Ours read differently from the partner's own rows at a glance.
      style={
        liftable
          ? undefined
          : { backgroundColor: 'color-mix(in srgb, var(--co-muted) 7%, transparent)' }
      }
    >
      {canEdit && (
        <td className={cell}>
          {active && liftable && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelectChange(player, e.target.checked)}
              aria-label={`Select ${player.account_id}`}
            />
          )}
        </td>
      )}
      <td className={`${cell} font-mono`}>{player.account_id}</td>
      <td className={cell}>
        <MatchBadge matchStatus={player.match_status} accountId={player.account_id} />
      </td>
      <td className={cell}>
        <Badge tone={active ? 'success' : 'neutral'}>{active ? 'Active' : 'Inactive'}</Badge>
      </td>
      <td className={mutedCell}>{ADDED_BY_LABEL[player.added_by] ?? player.added_by}</td>
      <td className={mutedCell}>{SOURCE_LABEL[player.source] ?? player.source}</td>
      <ReasonCell player={player} />
      <td className={mutedCell}>{new Date(player.created_at).toLocaleDateString()}</td>
      <td className={`${cell} text-right`}>
        {canEdit &&
          active &&
          (liftable ? (
            <button
              type="button"
              onClick={() => onDeactivate(player)}
              aria-label={`Deactivate ${player.account_id}`}
              className="rounded-lg border border-[var(--co-border)] px-2.5 py-1 text-[12px] font-medium text-[var(--co-text)] hover:border-[var(--co-error)] hover:text-[var(--co-error)]"
            >
              Deactivate
            </button>
          ) : (
            // Says why this row has no button while its neighbours do.
            <Badge tone="neutral" title={LOCKED_TITLE}>
              Locked by Repackz
            </Badge>
          ))}
      </td>
    </tr>
  );
}
